/**
 * Account UI Helpers
 *
 * Visual/presentation helpers for account roles.
 * Separated from business logic to keep domain logic free of UI imports.
 *
 * Used by: AccountSwitcher, AccountSwitcherDropdown, Sidebar, MobileNav,
 * ProfileButton, ProfileSection — any component needing role-based icons,
 * labels, or colors.
 *
 * @moved from src/utils/accountUIHelpers.ts (R19 — Fase 2 continuation)
 */

import { User, Users, GraduationCap, ShieldCheck } from 'lucide-react';
import { AccountRole } from '../types';
import { ROLE_COLORS } from '../../config/designTokens';

/**
 * Get the icon component for an account role.
 */
export function getAccountIcon(role: AccountRole) {
  switch (role) {
    case 'learner':
      return User;
    case 'parent':
      return Users;
    case 'teacher':
      return GraduationCap;
    case 'admin':
      return ShieldCheck;
  }
}

/**
 * Get the Dutch display label for an account role.
 */
export function getAccountRoleLabel(role: AccountRole): string {
  switch (role) {
    case 'learner':
      return 'Leerling';
    case 'parent':
      return 'Ouder';
    case 'teacher':
      return 'Docent';
    case 'admin':
      return 'Beheerder';
  }
}

/**
 * Color palette for account roles.
 * Unified color tokens used by both AccountSwitcher and AccountSwitcherDropdown.
 */
export const ACCOUNT_ROLE_COLORS: Record<AccountRole, {
  bg: string;
  text: string;
  border: string;
  iconBg: string;
  iconText: string;
  badgeBg: string;
  badgeText: string;
  hoverBorder: string;
}> = {
  learner: ROLE_COLORS.learner,
  parent: ROLE_COLORS.parent,
  teacher: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    iconBg: 'bg-violet-100',
    iconText: 'text-violet-600',
    badgeBg: 'bg-violet-100',
    badgeText: 'text-violet-700',
    hoverBorder: 'hover:border-violet-300'
  },
  admin: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-600',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700',
    hoverBorder: 'hover:border-amber-300'
  }
};